/**
 * Login View Component
 * Modern login page with background image and logo assets
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import * as Sentry from "@sentry/react";
import { BgLoginImage, SwarapointLoginLogo } from "@/assets/svg";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import { LoginUseCase, storeUserData } from "@/feature/auth";

export const LoginView = (): React.JSX.Element => {
  usePageTitle("Login");

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await LoginUseCase({
        email,
        password,
        deviceId: "device-uuid-" + Date.now(),
        deviceType: "WEB",
      });
      storeUserData(result.user);

      Sentry.setUser({
        id: result.user.userId,
        email: result.user.email,
        username: result.user.fullName,
      });

      // Redirect to dashboard
      navigate("/swarapoint/dashboard");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Login failed. Please check your credentials."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <BgLoginImage
          className="w-full h-full"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...({ preserveAspectRatio: "xMidYMid slice" } as any)}
        />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <SwarapointLoginLogo />
          </div>

          {/* Title */}
          <h1 className="text-xl font-semibold text-[#001A41] text-center mb-8">
            Login to Your Account
          </h1>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5 text-left">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email/Phone Number
              </label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email/Phone Number"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#001A41] focus:ring-1 focus:ring-[#001A41] transition-colors"
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#001A41] focus:ring-1 focus:ring-[#001A41] transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-none p-0"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-[#001A41] focus:ring-[#001A41] cursor-pointer"
              />
              <label
                htmlFor="rememberMe"
                className="text-sm text-gray-700 cursor-pointer select-none"
              >
                Remember Me
              </label>
            </div>

            {/* Login Button */}
            <Button
              htmlType="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              disabled={isLoading}
              className="w-full py-3"
            >
              Login
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
